import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
};

const dangerButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'rgba(220, 53, 69, 0.12)',
  border: '1px solid rgba(220, 53, 69, 0.25)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-error)',
  fontWeight: 600,
};

function InventoryForm({
  item,
  onCancel,
  onSave,
  saving,
}: {
  item?: any;
  onCancel: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState({
    name: item?.name || '',
    variation_name: item?.variation_name || 'Regular',
    description: item?.description || '',
    category: item?.category || '',
    sku: item?.sku || '',
    price: item?.price?.toString() || '',
    stock_nundah: String(item?.stock_nundah ?? 0),
    stock_mitchelton: String(item?.stock_mitchelton ?? 0),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          name: form.name.trim(),
          variation_name: form.variation_name.trim() || 'Regular',
          description: form.description.trim() || undefined,
          category: form.category.trim() || undefined,
          sku: form.sku.trim() || undefined,
          price: Number(form.price),
          stock_nundah: Number(form.stock_nundah),
          stock_mitchelton: Number(form.stock_mitchelton),
        });
      }}
      style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}
    >
      <div
        style={{
          display: 'grid',
          gap: 'var(--spacing-3)',
          gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
        }}
      >
        <div>
          <label style={labelStyle}>Product Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Variation</label>
          <input
            value={form.variation_name}
            onChange={(e) => setForm((c) => ({ ...c, variation_name: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Category</label>
          <input
            value={form.category}
            onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>SKU</label>
          <input
            value={form.sku}
            onChange={(e) => setForm((c) => ({ ...c, sku: e.target.value }))}
            style={inputStyle}
          />
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gap: 'var(--spacing-3)',
          gridTemplateColumns: '1.5fr 0.7fr 0.7fr 0.7fr',
          marginTop: 'var(--spacing-3)',
        }}
      >
        <div>
          <label style={labelStyle}>Description</label>
          <input
            value={form.description}
            onChange={(e) => setForm((c) => ({ ...c, description: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Price</label>
          <input
            type="number"
            value={form.price}
            onChange={(e) => setForm((c) => ({ ...c, price: e.target.value }))}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Nundah</label>
          <input
            type="number"
            value={form.stock_nundah}
            onChange={(e) => setForm((c) => ({ ...c, stock_nundah: e.target.value }))}
            required
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Mitchelton</label>
          <input
            type="number"
            value={form.stock_mitchelton}
            onChange={(e) => setForm((c) => ({ ...c, stock_mitchelton: e.target.value }))}
            required
            style={inputStyle}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)' }}>
        <button
          type="submit"
          disabled={saving}
          style={{ ...primaryButtonStyle, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving...' : item ? 'Update Inventory' : 'Add Inventory'}
        </button>
        <button type="button" onClick={onCancel} style={secondaryButtonStyle}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function Inventory() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [search, setSearch] = useState('');
  const [syncResult, setSyncResult] = useState<any | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<any | null>(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => api.getInventory(true),
  });

  const createMutation = useMutation({
    mutationFn: api.createInventory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowForm(false);
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateInventory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setEditingItem(null);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: api.deleteInventory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
  const syncMutation = useMutation({
    mutationFn: api.syncInventoryWithSquare,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setSyncError(null);
      setSyncResult(data);
      setTimeout(() => setSyncResult(null), 8000);
    },
    onError: (error: Error) => {
      setSyncResult(null);
      setSyncError(error.message || 'Square sync did not complete. Try again.');
    },
  });

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((item: any) =>
      [item.name, item.variation_name, item.category, item.sku].some((value) =>
        String(value || '')
          .toLowerCase()
          .includes(term),
      ),
    );
  }, [items, search]);

  return (
    <div className="inventory-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div>
          <h1>Inventory</h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-2)' }}>
            Manage retail stock in the admin portal and sync product details plus quantities with
            Square.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button
            onClick={() => {
              setSyncError(null);
              setSyncResult(null);
              syncMutation.mutate();
            }}
            disabled={syncMutation.isPending}
            style={{
              ...secondaryButtonStyle,
              borderColor: 'var(--color-accent)',
              color: 'var(--color-accent)',
            }}
          >
            {syncMutation.isPending ? 'Syncing...' : '⟳ Sync with Square'}
          </button>
          <button
            onClick={() => {
              setEditingItem(null);
              setShowForm(true);
            }}
            style={primaryButtonStyle}
          >
            + Add Item
          </button>
        </div>
      </div>

      {syncResult && (
        <div
          style={{
            ...cardStyle,
            marginBottom: 'var(--spacing-4)',
            backgroundColor: 'rgba(217, 178, 86, 0.08)',
            borderColor: 'rgba(217, 178, 86, 0.28)',
          }}
        >
          <strong>Sync complete.</strong> Imported: {syncResult.imported || 0}, updated:{' '}
          {syncResult.updated || 0}, pushed: {syncResult.pushed || 0}.
        </div>
      )}

      {syncError && (
        <div
          role="alert"
          style={{
            ...cardStyle,
            marginBottom: 'var(--spacing-4)',
            backgroundColor: 'rgba(220, 53, 69, 0.08)',
            borderColor: 'rgba(220, 53, 69, 0.2)',
            color: 'var(--color-error)',
          }}
        >
          <strong>Square sync did not complete.</strong> {syncError}
        </div>
      )}

      {(showForm || editingItem) && (
        <InventoryForm
          item={editingItem || undefined}
          saving={createMutation.isPending || updateMutation.isPending}
          onCancel={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          onSave={(data) => {
            if (editingItem) {
              updateMutation.mutate({ id: editingItem.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
        />
      )}

      <section style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <h2 style={{ margin: 0 }}>Stock</h2>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search inventory..."
            style={{ ...inputStyle, width: '240px' }}
          />
        </div>

        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)' }}>Loading inventory...</p>
        ) : filteredItems.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No inventory items found.</p>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
            {filteredItems.map((item: any) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 'var(--spacing-3)',
                  padding: 'var(--spacing-3)',
                  backgroundColor: item.square_item_id
                    ? 'rgba(217, 178, 86, 0.06)'
                    : 'var(--color-surface-elevated)',
                  border: `1px solid ${item.square_item_id ? 'rgba(217, 178, 86, 0.26)' : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-default)',
                  opacity: item.active ? 1 : 0.65,
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <strong>{item.name}</strong>
                    {item.variation_name && item.variation_name !== 'Regular' && (
                      <span style={{ color: 'var(--color-text-muted)' }}>
                        · {item.variation_name}
                      </span>
                    )}
                    <span
                      style={{
                        color: item.square_item_id
                          ? 'var(--color-success)'
                          : 'var(--color-text-muted)',
                        fontSize: '14px',
                      }}
                    >
                      {item.square_item_id ? '✓' : '○'}
                    </span>
                  </div>
                  <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                    ${Number(item.price || 0).toFixed(2)}
                    {item.category ? ` · ${item.category}` : ''}
                    {item.sku ? ` · SKU ${item.sku}` : ''}
                  </p>
                  <p style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                    Nundah {Number(item.stock_nundah || 0)} · Mitchelton{' '}
                    {Number(item.stock_mitchelton || 0)}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                  <button onClick={() => setEditingItem(item)} style={secondaryButtonStyle}>
                    Edit
                  </button>
                  <button onClick={() => setPendingDeactivate(item)} style={dangerButtonStyle}>
                    Deactivate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        isOpen={!!pendingDeactivate}
        title={`Deactivate ${pendingDeactivate?.name ?? 'item'}?`}
        message="This product will no longer be available in the inventory."
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (pendingDeactivate) deleteMutation.mutate(pendingDeactivate.id);
          setPendingDeactivate(null);
        }}
        onCancel={() => setPendingDeactivate(null)}
      />
    </div>
  );
}
